export interface OpenAiAdsContext {
    query: string;
    sourceUrl: string;
}

const OPPREF_SESSION_KEY = "openai_ads_oppref";
const OPPREF_COOKIE_KEY = "ht_openai_ads_oppref";
const MAX_OPPREF_LENGTH = 2048;
const OPENAI_SIGNUP_EVENT_TYPE = "registration_completed";

type OpenAiQueue = (
    command: "measure",
    eventName: typeof OPENAI_SIGNUP_EVENT_TYPE,
    data: { type: "customer_action" },
    options: { event_id: string }
) => void;

declare global {
    interface Window {
        oaiq?: OpenAiQueue;
    }
}

export function getOpenAiAdsContext(): OpenAiAdsContext {
    if (typeof window === "undefined") {
        return { query: "", sourceUrl: "" };
    }
    const params = new URLSearchParams(window.location.search);
    const oppref = getOpenAiClickReference();
    if (oppref) {
        params.set("oppref", oppref);
    }
    return {
        query: params.toString() ? "?" + params.toString() : "",
        sourceUrl: window.location.origin + window.location.pathname,
    };
}

export function captureOpenAiClickReference() {
    if (typeof window === "undefined") {
        return;
    }
    const oppref = new URLSearchParams(window.location.search).get("oppref");
    const normalizedOppref = normalizeOppref(oppref);
    if (!normalizedOppref) {
        return;
    }
    writeOpprefCookie(normalizedOppref);
    try {
        window.sessionStorage.setItem(OPPREF_SESSION_KEY, normalizedOppref);
    } catch (_error) {
        // Browser privacy modes can disable storage; the pixel still handles browser attribution.
    }
}

function readStoredOppref() {
    try {
        return window.sessionStorage.getItem(OPPREF_SESSION_KEY) || "";
    } catch (_error) {
        return "";
    }
}

export function measureSignupCompleted(eventId: string) {
    if (
        !eventId
        || typeof window === "undefined"
        || !getOpenAiClickReference()
        || typeof window.oaiq !== "function"
    ) {
        return;
    }
    window.oaiq(
        "measure",
        OPENAI_SIGNUP_EVENT_TYPE,
        { type: "customer_action" },
        { event_id: eventId }
    );
}

function getOpenAiClickReference() {
    return normalizeOppref(new URLSearchParams(window.location.search).get("oppref"))
        || normalizeOppref(readOpprefCookie())
        || normalizeOppref(readStoredOppref());
}

function readOpprefCookie() {
    return readCookie(OPPREF_COOKIE_KEY);
}

function readCookie(name: string) {
    try {
        const prefix = name + "=";
        const cookie = document.cookie
            .split(";")
            .map((value) => value.trim())
            .find((value) => value.startsWith(prefix));
        return cookie ? decodeURIComponent(cookie.substring(prefix.length)) : "";
    } catch (_error) {
        return "";
    }
}

function writeOpprefCookie(oppref: string) {
    try {
        const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = OPPREF_COOKIE_KEY + "=" + encodeURIComponent(oppref)
            + "; expires=" + expires
            + "; path=/; domain=.hellotracks.com; SameSite=Lax; Secure";
    } catch (_error) {
        // The URL and session storage paths can still preserve attribution.
    }
}

function normalizeOppref(value: string | null) {
    const normalized = value?.trim() || "";
    return normalized.length <= MAX_OPPREF_LENGTH ? normalized : "";
}
